import React, { FC, useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker-for-new-calendar';
import { Button } from '@mui/material';
import { addDays, addMonths, addYears } from 'date-fns';

import { Stack } from '../../Stack';
import { Typography } from '../../Typography';


type Range = {
    label: DayModifiers;
    result: DateRange;
};

enum DayModifiers {
    today = 'today',
    yesterday = 'yesterday',
    week = 'week',
    month = 'month',
    halfYear = 'halfYear',
    year = 'year',
}

const getRanges = (today: Date): Range[] => [
    {
        label: DayModifiers.today,
        result: { from: today, to: undefined },
    },
    {
        label: DayModifiers.yesterday,
        result: { from: addDays(today, -1), to: undefined },
    },
    {
        label: DayModifiers.week,
        result: { from: addDays(today, -7), to: today },
    },
    {
        label: DayModifiers.month,
        result: { from: addMonths(today, -1), to: today },
    },
    {
        label: DayModifiers.halfYear,
        result: { from: addMonths(today, -6), to: today },
    },
    {
        label: DayModifiers.year,
        result: { from: addYears(today, -1), to: today },
    },
];

type RangesProps = {
    locale: 'ru' | 'en';
    value: DateRange;
    onChange: (value: DateRange) => void;
};

const localeRangeLabel: Record<
RangesProps['locale'],
Record<DayModifiers, string>
> = {
    en: {
        today: 'Today',
        yesterday: 'Yesterday',
        week: 'Week',
        month: 'Month',
        halfYear: 'Half year',
        year: 'Year',
    },
    ru: {
        today: 'Сегодня',
        yesterday: 'Вчера',
        week: 'Неделя',
        month: 'Месяц',
        halfYear: 'Полгода',
        year: 'Год',
    },
};

const isEqualDate = (date1: Date, date2: Date): boolean =>
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();

type RangeButtonProps = {
    data: Range;
    locale: 'ru' | 'en';
    value: DateRange;
    onChange: (date: DateRange) => void;
};

const RangeButton: FC<RangeButtonProps> = ({
    data: { label, result },
    locale,
    value,
    onChange,
}) => {
    const onClick = useCallback(() => onChange(result), [result, onChange]);
    const selected = useMemo(
        () =>
            value?.from &&
            isEqualDate(value.from, result.from) &&
            (!result.to || value.to && isEqualDate(value.to, result.to)),
        [value, result],
    );
    return (
        <Button
            onClick={onClick}
            sx={{
                py: 1,
                px: 4,
                color: selected ? 'grayscale.shade0' : 'grayscale.shade95',
                bgcolor: selected ? 'general.primary50' : 'grayscale.shade0',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: selected ? 'transparent' : 'grayscale.shade80',
                borderRadius: 2.5,

                '&:hover': {
                    bgcolor: selected
                        ? 'general.primary60'
                        : 'grayscale.shade10',
                },
                '&:active': {
                    bgcolor: selected
                        ? 'general.primary80'
                        : 'grayscale.shade20',
                },
            }}
        >
            <Typography variant='mediumBody2'>
                {localeRangeLabel[locale][label]}
            </Typography>
        </Button>
    );
};

export const Ranges: FC<RangesProps> = React.memo(
    ({ locale, value, onChange }) => {
        const ranges = useMemo(() => getRanges(new Date()), []);
        return (
            <Stack spacing={2} px={2} pb={5} direction='row'>
                {ranges.map(r => (
                    <RangeButton
                        key={r.label}
                        data={r}
                        locale={locale}
                        value={value}
                        onChange={onChange}
                    />
                ))}
            </Stack>
        );
    },
);
